import { useLocation, useNavigate } from "@solidjs/router";
import { Logo } from "@storytell/core";
import { StButton } from "@storytell/ui/buttons";
import { LayoutProvider } from "@storytell/ui/layout";
import { TbLoader2, TbMenu2 } from "solid-icons/tb";
import { createEffect, createSignal, on, onCleanup, type ParentComponent, Show, Suspense } from "solid-js";
import { isServer } from "solid-js/web";
import { ActionRunsDrawer } from "~/components/actions/ActionRunsDrawer/ActionRunsDrawer";
import { ChatScrollProvider, useChatScrollContextSafe } from "~/components/chat/contexts/ChatScrollContext";
import { TOCProvider } from "~/components/chat/contexts/TOCContext";
import { PromptTimeline } from "~/components/chat/PromptTimeline";
import { OfflineBanner } from "~/components/connectivity";
import { ErrorBoundary } from "~/components/error";
import { ProjectHomeCreditsPill } from "~/components/homepage/ProjectHomeCreditsPill";
import { LogoLoading } from "~/components/loading/LogoLoading";
import { NotificationPanel } from "~/components/notifications/NotificationPanel";
import { ToastHost } from "~/components/notifications/ToastHost";
import { ProjectSwitcherModal } from "~/components/projects/ProjectSwitcherModal";
import { RebrandBanner } from "~/components/shared/RebrandBanner";
import { useChatUrlParameter } from "~/hooks/useChatUrlParameter";
import { useChatwithUrlParameter } from "~/hooks/useChatwithUrlParameter";
import { useHasPairedDesktopDevice } from "~/hooks/useHasPairedDesktopDevice";
import { usePanelResize } from "~/hooks/usePanelResize";
import { checkAndExecutePendingAction, getPendingAuthAction } from "~/hooks/usePendingAuthAction";
import { useProjectUrlSync } from "~/hooks/useProjectUrlSync";
import { useI18n } from "~/i18n/context";
import { translateWithJSX } from "~/i18n/utils";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";
import { NavBar } from "../navigation/NavBar";
import { AppLayoutDropzone } from "./AppLayoutDropzone";
import { MainContent } from "./MainContent";
import { AppUIContextProvider } from "./minimal/AppUIContext";
import { isSettingsPath, SettingsContent } from "./SettingsShell";
import { SidePanel } from "./SidePanel";
import { SidePanelToggleButton } from "./SidePanelToggleButton";

/** Routes that bypass AppLayout entirely (no navbar, no layout - completely standalone) */
const STANDALONE_ROUTES = [
  "/app",
  // An invitation is answered before the invitee has a scope to render chrome
  // for, and a path listed in none of this array, SETTINGS_PATTERNS or
  // NAVBAR_ONLY never mounts its route children at all.
  "/invites",
  "/design",
  "/icons",
  "/record",
  "/start-live-session",
  "/actions",
  "/chats",
  "/files",
  "/labels",
  "/concepts",
  "/collections",
  "/skills",
  "/personas",
  "/search",
  "/live-sessions",
];

/** Route patterns that bypass AppLayout entirely */
const STANDALONE_PATTERNS = [
  /^\/auth\//,
  /^\/shared\//,
  /^\/actions\/unsubscribe(\/|$)/,
  // A calendar invite's session link resolves its project before any chrome
  // has a scope to render for.
  /^\/live-sessions\/[^/]+$/,
];

/** Routes that should render with navbar only (no top bar or main/side panels) */
const NAVBAR_ONLY_ROUTES = ["/upgrade", "/notifications"];

/** Route patterns that should render with navbar only (supports dynamic segments) */
const NAVBAR_ONLY_PATTERNS = [
  /^\/projects\/[^/]+(\/.*)?$/,
  /^\/project\/[^/]+\/sharing$/,
  /^\/project\/[^/]+\/billing$/,
];

const TRANSITION_DURATION_MS = 200;
const NAV_TRANSITION_EASING = "cubic-bezier(0.4, 0, 0.2, 1)";
const RESIZE_HANDLE_WIDTH = 8; // Pixels on each side of the border for resize detection

/** Shown when user is on a project URL but has no access (guest or project not in list). */
function ProjectNoAccessView() {
  const wire = useWire();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [projectsModalOpen, setProjectsModalOpen] = createSignal(false);
  return (
    <div class="scrollbar-show-on-hover flex h-full min-w-0 flex-auto items-center justify-center overflow-auto py-12">
      <Show
        when={wire.auth?.isGuest?.()}
        fallback={
          <div class="px-4 text-center">
            <h2 class="mb-1 text-3xl font-bold text-primary-text">{t("project_detail_not_found")}</h2>
            <p class="mb-4 text-lg font-bold text-primary-text">{t("project_detail_not_found_or_no_access")}</p>
            <p class="mb-2 text-lg text-primary-text">
              {translateWithJSX(t, "project_detail_view_projects", {
                availableProjects: (
                  <button
                    type="button"
                    onClick={() => setProjectsModalOpen(true)}
                    class="font-medium text-brand underline hover:text-brand-hover"
                  >
                    {t("project_detail_available_projects")}
                  </button>
                ),
              })}
            </p>
            <p class="mt-4 text-base text-primary-text">
              <span class="font-bold">{t("project_detail_need_help")}</span> {t("project_detail_contact_owner_or")}
              <a
                href="mailto:support@storytell.ai?subject=Need help with Projects&body=Describe your issue here..."
                class="font-medium text-brand underline hover:text-brand-hover"
              >
                {t("project_detail_email_us")}
              </a>
            </p>
          </div>
        }
      >
        <div class="flex flex-col items-center justify-center">
          <div class="mb-8">
            <Logo size={30} />
          </div>
          <p class="mb-6 text-lg font-medium text-primary-text">{t("project_detail_sign_in_required")}</p>
          <div class="flex items-center justify-center gap-2">
            <StButton
              onClick={() => navigate("/auth/signup")}
              class="rounded-lg bg-brand px-6 py-2 font-medium text-on-brand hover:bg-brand-hover"
            >
              {t("project_detail_sign_up")}
            </StButton>
            <span class="text-secondary-text">{t("common.or")}</span>
            <a
              href="/auth/signin"
              onClick={(e) => {
                e.preventDefault();
                navigate("/auth/signin");
              }}
              class="font-medium text-brand underline hover:text-brand-hover"
            >
              {t("action.sign_in")}
            </a>
          </div>
        </div>
      </Show>
      <ProjectSwitcherModal open={projectsModalOpen()} onOpenChange={setProjectsModalOpen} />
    </div>
  );
}

/** Floating mini timeline on the right edge of the chat viewport, visible when the sidebar is hidden. */
function FloatingChatTimeline(props: { isSidePanelHidden: () => boolean }) {
  const chatScroll = useChatScrollContextSafe();

  return (
    <Show when={chatScroll?.hasRoomForTimeline() && props.isSidePanelHidden() && chatScroll?.chatId()}>
      <div
        class="fixed inset-y-0 z-20 flex items-center"
        style={{ right: `${chatScroll?.timelineRightInset() ?? 40}px` }}
      >
        <PromptTimeline
          scrollContainerRef={() => chatScroll?.scrollContainerRef()}
          chatId={() => chatScroll?.chatId() ?? undefined}
          forceVisible={true}
          centerInViewport={true}
        />
      </div>
    </Show>
  );
}

/**
 * Notifications hang off the sidebar's right edge rather than the content's, so
 * checking them never reflows the page underneath. Fixed width by design: this is
 * a transient reading surface, not a second content column.
 */
const NOTIFICATIONS_DRAWER_WIDTH = 400;

function NotificationsDrawer() {
  const wire = useWire();
  const layout = wire.ui.layout;

  createEffect(() => {
    if (!layout.notificationsOpen()) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") layout.setNotificationsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    onCleanup(() => document.removeEventListener("keydown", onKeyDown));
  });

  // The rail and the expanded nav are both in flow, so the drawer's left edge is
  // simply the nav's current width — including mid-drag.
  const leftInset = () => (layout.isSmallScreen() ? 0 : layout.navbarSize());

  return (
    <Show when={layout.notificationsOpen()}>
      {/* Click-catcher only, deliberately undimmed: the point of a drawer over a
          page is that the page stays readable behind it. */}
      <div role="presentation" class="fixed inset-0 z-40" onClick={() => layout.setNotificationsOpen(false)} />
      <aside
        class="fixed inset-y-0 z-40 flex flex-col border-r border-primary-border bg-panel shadow-2xl"
        style={{
          left: `${leftInset()}px`,
          width: `min(${NOTIFICATIONS_DRAWER_WIDTH}px, calc(100vw - ${leftInset()}px - 2rem))`,
        }}
      >
        <LayoutProvider side>
          <NotificationPanel />
        </LayoutProvider>
      </aside>
    </Show>
  );
}

export const AppLayout: ParentComponent = (props) => {
  const wire = useWire();
  const { t } = useI18n();
  const layout = wire.ui.layout;
  const nav = wire.navigation;
  const location = useLocation();
  const hasPairedDesktopDevice = useHasPairedDesktopDevice();

  // Handle ?chat= URL parameter for auto-submitting prompts
  useChatUrlParameter();
  // Handle ?chatwith= asset ID to add that asset to chat scope (e.g. from upload modal "Chat")
  useChatwithUrlParameter();
  // Sync project context with URL - auto-switch when visiting a URL with different projectId
  const { isSynced: isProjectSynced, showProjectNoAccess } = useProjectUrlSync();

  // Swap favicon between idle and working states based on chat streaming
  createEffect(() => {
    if (isServer) return;
    const isWorking = wire.chat.isChatStreaming();
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) {
      link.href = isWorking ? "/favicon-working.svg" : "/favicon.svg";
    }
  });

  /** Check if current route should bypass layout entirely (standalone pages like auth, pricing) */
  const isStandaloneRoute = () =>
    STANDALONE_ROUTES.some((route) => location.pathname === route) ||
    STANDALONE_PATTERNS.some((pattern) => pattern.test(location.pathname));

  /** Check if the sidebar and content column render in settings mode */
  const isSettingsRoute = () => isSettingsPath(location.pathname);

  /** Check if current route should only show navbar */
  const isNavbarOnlyRoute = () =>
    NAVBAR_ONLY_ROUTES.some((route) => location.pathname === route) ||
    NAVBAR_ONLY_PATTERNS.some((pattern) => pattern.test(location.pathname));

  // Track if side panel is effectively hidden (no content, minimized via URL, or user minimized)
  const isSidePanelHidden = () =>
    layout.sideCollapsed() || layout.sideMinimized() || layout.sideEmpty() || nav.isSideHidden();

  // Track visual collapsed state - updates after transition completes
  const [sideVisuallyCollapsed, setSideVisuallyCollapsed] = createSignal(
    layout.sideCollapsed() || layout.sideMinimized()
  );

  createEffect(
    on(isSidePanelHidden, (hidden) => {
      if (hidden) {
        // Wait for transition to complete before visually collapsing (hiding border)
        const id = setTimeout(() => setSideVisuallyCollapsed(true), TRANSITION_DURATION_MS);
        onCleanup(() => clearTimeout(id));
      } else {
        // Show border immediately when expanding
        setSideVisuallyCollapsed(false);
      }
    })
  );

  // Auto-collapse navbar on small screens
  createEffect(
    on(
      () => layout.isSmallScreen(),
      (isSmall) => {
        if (isSmall && !layout.navbarCollapsed()) {
          layout.setNavbarCollapsed(true);
        }
      }
    )
  );

  // URL param ?nav=closed: start with the nav bar collapsed (e.g. for shared links or embedding).
  // Apply only once per navigation so that when the user expands the nav it stays expanded;
  // otherwise the effect would re-run (location.query.nav is still "closed") and force collapse again.
  const [didApplyNavClosedForCurrentUrl, setDidApplyNavClosedForCurrentUrl] = createSignal(false);
  createEffect(() => {
    const navClosed = location.query.nav === "closed";
    if (!navClosed) {
      setDidApplyNavClosedForCurrentUrl(false);
      return;
    }
    if (!didApplyNavClosedForCurrentUrl()) {
      layout.setNavbarCollapsed(true);
      setDidApplyNavClosedForCurrentUrl(true);
    }
  });

  // Check and execute pending auth action after authentication
  // Uses a flag to ensure we only execute once per session
  const [hasCheckedPendingAction, setHasCheckedPendingAction] = createSignal(false);
  createEffect(
    on(
      () => wire.auth.isRegistered(),
      (isRegistered) => {
        // Only check when user becomes registered and we haven't checked yet
        if (isRegistered && !hasCheckedPendingAction()) {
          const pending = getPendingAuthAction();
          if (pending) {
            setHasCheckedPendingAction(true);
            // Delay execution to ensure the PromptBar and other components are mounted
            const id = setTimeout(() => {
              checkAndExecutePendingAction();
            }, 500);
            onCleanup(() => clearTimeout(id));
          }
        }
      }
    )
  );

  // Ref for the flex container that holds main + resize handle + side panel (used to detect "main squeezed" for promote)
  let mainSideContainerRef: HTMLDivElement | undefined;

  // Side panel resize: when dragged small (right) → hide; when main squeezed (drag left past threshold) → promote to main view
  const sideResize = usePanelResize({
    getSize: () => layout.sideExpandedSize(),
    onResize: layout.resizeSide,
    direction: "left",
    isDisabled: isSidePanelHidden,
    collapseThreshold: layout.SIDE_COLLAPSE_THRESHOLD,
    onCollapseThreshold: () => nav.hideSidePanel(),
    getContainerWidth: () => mainSideContainerRef?.clientWidth ?? 0,
    mainMinThreshold: 150,
    onPromoteThreshold: () => nav.hideSidePanel(),
  });

  // Navbar resize
  const navResize = usePanelResize({
    getSize: () => layout.navbarSize(),
    onResize: layout.resizeNavbar,
    direction: "right",
    isDisabled: () => layout.navbarCollapsed(),
  });

  const transitionStyle = () =>
    sideResize.isResizing() || navResize.isResizing()
      ? undefined
      : `width ${TRANSITION_DURATION_MS}ms ${NAV_TRANSITION_EASING}`;

  return (
    <AppUIContextProvider>
      {/* Standalone routes bypass all layout (auth, pricing, etc.) */}
      <Show when={isStandaloneRoute()}>{props.children}</Show>

      {/* Routes with navbar */}
      <Show when={!isStandaloneRoute()}>
        {/* Settings pages have nothing to upload into, so they take the shell without the drop target. */}
        <Show when={!isSettingsRoute()}>
          <AppLayoutDropzone />
        </Show>
        <div class="flex h-dvh max-h-dvh flex-col overflow-hidden">
          <OfflineBanner />
          <ToastHost />
          <Show when={!hasPairedDesktopDevice()}>
            <RebrandBanner />
          </Show>
          {/* On a small screen the aside has no rail to hover, so this is the only way back to it. */}
          <Show when={layout.isSmallScreen()}>
            <div class="flex h-12 shrink-0 items-center bg-canvas px-3">
              <button
                type="button"
                onClick={() => {
                  trackFeature({ feature: "nav.sidebar_toggle", via: "button" });
                  layout.setNavbarCollapsed(false);
                }}
                aria-label={t("nav_open_menu")}
                class="rounded-md border border-primary-border bg-panel p-2 text-secondary-text transition-colors hover:bg-canvas-hover hover:text-primary-text"
              >
                <TbMenu2 class="size-5" />
              </button>
            </div>
          </Show>
          <div class="relative flex min-h-0 flex-1">
            {/* Backdrop for navbar drawer on small screens */}
            <Show when={layout.isSmallScreen() && !layout.navbarCollapsed()}>
              <div
                role="presentation"
                class="fixed inset-0 z-30 bg-black/10"
                onClick={() => layout.setNavbarCollapsed(true)}
              />
            </Show>

            {/* Desktop: the nav is always in flow, so the wrapper carries its width and
                content sits beside it. Mobile: the wrapper has no width and the aside
                overlays as a drawer. */}
            <div
              class="relative shrink-0"
              style={{
                width: layout.isSmallScreen() ? "0px" : `${layout.navbarSize()}px`,
                transition: transitionStyle(),
              }}
            >
              <aside
                class="h-full overflow-hidden border-r border-primary-border bg-panel"
                classList={{
                  "fixed left-0 inset-y-0  z-40 shadow-xl": layout.isSmallScreen() && !layout.navbarCollapsed(),
                }}
                style={{
                  width: layout.isSmallScreen()
                    ? layout.navbarCollapsed()
                      ? "0px"
                      : `${layout.navbarSize()}px`
                    : "100%",
                  transition: transitionStyle(),
                }}
              >
                <NavBar />
                {/* Navbar resize handle - inside aside on non-mobile so it stays at overlay edge */}
                <Show when={!layout.navbarCollapsed() && !layout.isSmallScreen()}>
                  <div
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={t("aria_label_resize_handle")}
                    class="absolute inset-y-0 right-0 z-10 flex cursor-col-resize items-center justify-center"
                    style={{
                      width: `${RESIZE_HANDLE_WIDTH * 2}px`,
                    }}
                    onMouseEnter={() => navResize.setHovering(true)}
                    onMouseLeave={() => navResize.setHovering(false)}
                    onMouseDown={navResize.handleStart}
                    onDblClick={() => layout.resizeNavbar(layout.NAV_DEFAULT)}
                  >
                    <div
                      class="h-full w-1 transition-colors duration-150"
                      classList={{
                        "bg-brand/30": navResize.isResizing(),
                        "bg-transparent": !navResize.isResizing(),
                      }}
                    />
                  </div>
                </Show>
              </aside>
            </div>

            <NotificationsDrawer />

            {/* Settings mode: the sidebar carries the navigation, the column carries the page */}
            <Show when={isSettingsRoute()}>
              <div class="scrollbar-show-on-hover min-w-0 flex-auto overflow-auto">
                <SettingsContent>{props.children}</SettingsContent>
              </div>
            </Show>

            {/* Navbar-only routes: render children without main/side panels */}
            <Show when={!isSettingsRoute() && isNavbarOnlyRoute()}>
              <div class="flex min-w-0 flex-auto flex-col">
                <div class="scrollbar-show-on-hover flex-auto overflow-auto">{props.children}</div>
              </div>
            </Show>

            {/* Standard layout with main/side panels */}
            <Show when={!isSettingsRoute() && !isNavbarOnlyRoute()}>
              <div class="flex min-w-0 flex-auto flex-col">
                <TOCProvider>
                  <ChatScrollProvider>
                    <div
                      ref={(el) => {
                        mainSideContainerRef = el;
                      }}
                      class="relative flex min-h-0 flex-1 overflow-hidden"
                    >
                      {/* Show loading spinner while project context is syncing with URL */}
                      <Show
                        when={isProjectSynced()}
                        fallback={
                          <div class="flex h-full flex-auto items-center justify-center">
                            <TbLoader2 class="size-6 animate-spin text-secondary-text" />
                          </div>
                        }
                      >
                        {/* When on a project URL without access, show sign-in or project not found */}
                        <Show
                          when={!showProjectNoAccess()}
                          fallback={
                            <div class="relative min-w-0 flex-auto" data-main-scroll>
                              <ProjectNoAccessView />
                            </div>
                          }
                        >
                          <div
                            class="scrollbar-show-on-hover relative h-full min-w-0 flex-auto overflow-auto"
                            data-main-scroll
                          >
                            <LayoutProvider main>
                              {/* Scope suspense and render errors to the content area so the app
                                  chrome stays visible instead of pinning the whole app to the boot
                                  splash. LogoLoading is the consistent in-content loader; its
                                  bg-canvas also prevents a body-bg flash on nav. */}
                              <Suspense fallback={<LogoLoading />}>
                                <ErrorBoundary>
                                  <MainContent />
                                </ErrorBoundary>
                              </Suspense>
                            </LayoutProvider>
                          </div>

                          {/* Resize handle - only show when side panel has content, isn't hidden, and on large screens */}
                          <Show when={nav.state().sideId && !isSidePanelHidden() && layout.isLargeScreen()}>
                            <div
                              role="separator"
                              aria-orientation="vertical"
                              aria-label={t("aria_label_resize_handle")}
                              class="group absolute inset-y-0 z-10 flex cursor-col-resize items-center justify-center"
                              style={{
                                right: `${layout.sideSize() - RESIZE_HANDLE_WIDTH}px`,
                                width: `${RESIZE_HANDLE_WIDTH * 2}px`,
                              }}
                              onMouseEnter={() => sideResize.setHovering(true)}
                              onMouseLeave={() => sideResize.setHovering(false)}
                              onMouseDown={sideResize.handleStart}
                            >
                              {/* Visual indicator line */}
                              <div
                                class="h-full w-1 transition-colors duration-150"
                                classList={{
                                  "bg-brand/30": sideResize.isHovering() || sideResize.isResizing(),
                                  "bg-transparent": !sideResize.isHovering() && !sideResize.isResizing(),
                                }}
                              />
                              {/* Grip handle with 3 vertical dots */}
                              <div
                                class="absolute flex -translate-x-px flex-col items-center justify-center gap-1 rounded-full border border-primary-border bg-panel px-1 py-2 transition-shadow duration-150"
                                classList={{
                                  "ring-2 ring-brand/30": sideResize.isHovering() || sideResize.isResizing(),
                                }}
                              >
                                <div class="size-1 rounded-full bg-gray-400" />
                                <div class="size-1 rounded-full bg-gray-400" />
                                <div class="size-1 rounded-full bg-gray-400" />
                              </div>
                            </div>
                          </Show>

                          {/* Backdrop for side panel on medium/small screens */}
                          <Show when={!layout.isLargeScreen() && nav.state().sideId && !isSidePanelHidden()}>
                            <div
                              role="presentation"
                              class="fixed inset-0 z-30 bg-black/10"
                              onClick={() => nav.hideSidePanel()}
                            />
                          </Show>

                          {/* Side panel */}
                          <SidePanel
                            isHidden={isSidePanelHidden}
                            isVisuallyCollapsed={sideVisuallyCollapsed}
                            transitionStyle={transitionStyle}
                          />

                          {/* Side panel toggle button - overlay anchored to the panel's left edge,
                              mirroring the inset that the close/expand controls used on the right corner.
                              When the panel is hidden (sideSize = 0), the max() clamp keeps the button at the
                              parent's right edge so it remains reachable to reopen the panel. */}
                          <div
                            class="absolute top-2.75 z-50"
                            style={{
                              right: `max(0.6875rem, calc(${layout.sideSize()}px - 2.5rem))`,
                              transition: sideResize.isResizing()
                                ? undefined
                                : `right ${TRANSITION_DURATION_MS}ms ${NAV_TRANSITION_EASING}`,
                            }}
                          >
                            <SidePanelToggleButton isHidden={isSidePanelHidden} />
                          </div>

                          <FloatingChatTimeline isSidePanelHidden={isSidePanelHidden} />

                          <ProjectHomeCreditsPill />

                          <ActionRunsDrawer />
                        </Show>
                      </Show>
                    </div>
                  </ChatScrollProvider>
                </TOCProvider>
              </div>
            </Show>
          </div>
        </div>
      </Show>
    </AppUIContextProvider>
  );
};
